// FoodStation Links — constantes visuais e fallbacks locais.
// Os dados reais da página são carregados pela API do LinkPage.
import { ATENDABOT_PRODUCT_IMAGE, PRODUCT_IMAGES } from '../../../branding/assets';

// Paleta base alinhada ao tema do produto (theme.js)
export const PALETTE = {
  primary: '#003366', // Azul Petróleo
  secondary: '#FF7043', // Laranja Coral
  success: '#2ECC71', // Verde Esmeralda
  ink: '#0e1b2a',
  muted: '#5b6b7d',
};

// URL pública exibida na configuração.
export const PUBLIC_BASE = 'foodstation.app/links';

// Produtos FoodStation disponíveis para virar botão na página pública.
export const FOODSTATION_PRODUCTS = [
  {
    id: 'reservo',
    accessKey: 'reservo',
    product: 'Reservo',
    description: 'Link de reservas',
    defaultLabel: 'Reservar mesa',
    icon: PRODUCT_IMAGES.reservoIcon,
    emoji: '📅',
  },
  {
    id: 'reservo_waitlist',
    accessKey: 'reservo',
    product: 'Reservo — Fila de espera',
    description: 'Link para o cliente entrar na fila de espera',
    defaultLabel: 'Entrar na fila de espera',
    defaultEnabled: false,
    hiddenWhenUnavailable: true,
    icon: PRODUCT_IMAGES.reservoIcon,
    emoji: '⏳',
  },
  {
    id: 'onemenu',
    accessKey: 'cardapio',
    product: 'One Menu',
    description: 'Link do cardápio digital',
    defaultLabel: 'Ver cardápio',
    icon: PRODUCT_IMAGES.oneMenu,
    emoji: '🍝',
  },
  {
    id: 'feedbackhero',
    accessKey: 'feedback_hero',
    product: 'Feedback Hero',
    description: 'Link de avaliação',
    defaultLabel: 'Avaliar experiência',
    icon: PRODUCT_IMAGES.feedbackHero,
    emoji: '⭐',
  },
  {
    id: 'curriculocerto',
    accessKey: 'cv',
    product: 'Currículo Certo',
    description: 'Link de vagas / trabalhe conosco',
    defaultLabel: 'Trabalhe conosco',
    icon: PRODUCT_IMAGES.cv,
    emoji: '💼',
  },
  {
    id: 'atendabot',
    accessKey: 'atendabot',
    product: 'AtendaBot',
    description: 'Link de atendimento no WhatsApp',
    defaultLabel: 'Falar no WhatsApp',
    icon: ATENDABOT_PRODUCT_IMAGE,
    emoji: '💬',
  },
];

// -------------------------------------------------------------------------
// Aparência (temas / cor de destaque / estilo de botão) — inspirado nas
// galerias de tema do Linktree e concorrentes.
// -------------------------------------------------------------------------

// Cada tema é um "mood" coeso (claro/escuro + capa). `cover` é um CSS
// background aplicado na faixa de capa. As demais cores dos botões são
// derivadas de `accent` + `buttonStyle` (ver theme.js).
export const THEME_PRESETS = [
  {
    id: 'petroleo',
    name: 'Petróleo',
    mode: 'light',
    pageBg: '#0b1a30',
    cardBg: '#f5f7fb',
    text: '#0e1b2a',
    muted: '#5b6b7d',
    divider: 'rgba(14,27,42,0.08)',
    coverStart: '#1b6ea3',
    coverEnd: '#071e33',
    cover:
      'radial-gradient(120% 140% at 50% -20%, #1b6ea3 0%, #0a2a43 55%, #071e33 100%)',
    logoRing: '#f5f7fb',
    defaultAccent: '#003366',
    defaultButtonStyle: 'soft',
  },
  {
    id: 'escuro',
    name: 'Escuro',
    mode: 'dark',
    pageBg: '#0a1526',
    cardBg: '#132339',
    text: '#eaf1fb',
    muted: '#9db0cc',
    divider: 'rgba(255,255,255,0.09)',
    coverStart: '#1f3a5c',
    coverEnd: '#0d1a2e',
    cover:
      'radial-gradient(120% 140% at 50% -10%, #1f3a5c 0%, #14263f 50%, #0d1a2e 100%)',
    logoRing: '#132339',
    defaultAccent: '#ff7a3d',
    defaultButtonStyle: 'outline',
  },
  {
    id: 'esmeralda',
    name: 'Esmeralda',
    mode: 'dark',
    pageBg: '#0a2018',
    cardBg: '#123528',
    text: '#eef7f0',
    muted: '#a7cbb7',
    divider: 'rgba(255,255,255,0.09)',
    coverStart: '#1f7a54',
    coverEnd: '#0c2b1f',
    cover:
      'radial-gradient(120% 140% at 50% -10%, #1f7a54 0%, #124430 55%, #0c2b1f 100%)',
    logoRing: '#123528',
    defaultAccent: '#e0b25a',
    defaultButtonStyle: 'solid',
  },
  {
    id: 'coral',
    name: 'Coral',
    mode: 'light',
    pageBg: '#2a1512',
    cardBg: '#fff8f4',
    text: '#2a1712',
    muted: '#8a6f64',
    divider: 'rgba(42,23,18,0.08)',
    coverStart: '#ffb27a',
    coverEnd: '#d84e2a',
    cover:
      'radial-gradient(120% 140% at 50% -20%, #ffb27a 0%, #ff7043 45%, #d84e2a 100%)',
    logoRing: '#fff8f4',
    defaultAccent: '#ff7043',
    defaultButtonStyle: 'solid',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    mode: 'light',
    pageBg: '#dfe4ec',
    cardBg: '#ffffff',
    text: '#12203a',
    muted: '#6b7890',
    divider: 'rgba(18,32,58,0.08)',
    coverStart: '#eef2f8',
    coverEnd: '#dfe6f1',
    cover:
      'linear-gradient(150deg, #eef2f8 0%, #dfe6f1 100%)',
    logoRing: '#ffffff',
    defaultAccent: '#003366',
    defaultButtonStyle: 'soft',
  },
];

// Cores de destaque selecionáveis (dots).
export const ACCENTS = [
  { id: 'petroleo', value: '#003366', name: 'Petróleo' },
  { id: 'coral', value: '#ff7043', name: 'Coral' },
  { id: 'esmeralda', value: '#2ecc71', name: 'Esmeralda' },
  { id: 'ambar', value: '#f6a723', name: 'Âmbar' },
  { id: 'roxo', value: '#7c5cff', name: 'Roxo' },
  { id: 'rosa', value: '#ec4899', name: 'Rosa' },
];

// Estilo dos botões.
export const BUTTON_STYLES = [
  { id: 'solid', name: 'Sólido' },
  { id: 'outline', name: 'Contorno' },
  { id: 'soft', name: 'Suave' },
];

export const DEFAULT_THEME = {
  presetId: 'escuro',
  accent: '#ff7a3d',
  buttonStyle: 'soft',
  showIcons: true, // exibir logos/fotos nos botões
  customColors: {
    enabled: false,
  },
};

// Restaurante fictício usado como demonstração.
export const MOCK_RESTAURANT = {
  slug: 'bellavista',
  name: 'Restaurante Bella Vista',
  tagline: 'Reservas, cardápio, atendimento e muito mais em um só lugar.',
  monogram: 'BV',
  address: 'Av. Dr. Maurício Cardoso, 1620 · Porto Alegre',
  hours: 'Ter–Sex 18h às 23h · Sáb e Dom 12h às 23h',
  verified: true,
  coverImage: null, // data URL (mock) — foto de capa opcional
  profileImage: null, // data URL (mock) — foto de perfil/logo opcional
  showAddress: true, // exibir o endereço na página pública
  showHours: true, // exibir o horário na página pública
  showDirections: true, // exibir o botão "Como chegar" (Google Maps)
};

// URL de rota do Google Maps para o endereço (mock — abre o Maps).
export const directionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || '')}`;

// Estado inicial dos botões de produto (todos ativos por padrão, com o
// label sugerido). O restaurante pode editar o texto e ligar/desligar.
export const DEFAULT_PRODUCT_STATE = FOODSTATION_PRODUCTS.map((p) => ({
  id: p.id,
  enabled: p.defaultEnabled !== false,
  label: p.defaultLabel,
}));

// Links externos de exemplo (mock).
export const DEFAULT_EXTERNAL_LINKS = [
  { id: 'ext-instagram', label: 'Nosso Instagram', url: 'https://instagram.com/bellavista' },
  { id: 'ext-ifood', label: 'Peça no iFood', url: 'https://ifood.com.br/bellavista' },
];

export const makeExternalId = () =>
  `ext-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
